import { Language } from '../contexts/LanguageContext';

type TranslationType = {
  [key: string]: string | TranslationType;
};

const translations: Record<Language, TranslationType> = {
  en: {
    // Header
    title: 'QR Code Generator',
    subtitle: 'Create beautiful, customizable QR codes for your business and personal needs.',
    
    // Navigation
    generate: 'Generate',
    history: 'History',
    
    // History Page
    historyPage: {
      title: 'QR Code History',
      empty: {
        title: 'No QR Codes Yet',
        description: 'Generated QR codes will appear here for easy access and re-download.'
      },
      clearAll: 'Clear All',
      download: 'Download',
      unknownTime: 'Unknown time',
      qrCodeAlt: 'QR Code',
      types: {
        url: 'Website URL',
        text: 'Plain Text',
        email: 'Email',
        phone: 'Phone',
        sms: 'SMS',
        wifi: 'WiFi'
      }
    },
    
    // QR Types
    qrCodeType: 'QR Code Type',
    website: 'Website URL',
    websiteDesc: 'Link to any website',
    plainText: 'Plain Text',
    plainTextDesc: 'Any text content',
    email: 'Email',
    emailDesc: 'Send an email',
    phone: 'Phone',
    phoneDesc: 'Make a phone call',
    sms: 'SMS',
    smsDesc: 'Send a text message',
    wifi: 'WiFi',
    wifiDesc: 'Connect to WiFi',
    
    // Input Form
    enterContent: 'Enter Your Content',
    websiteUrl: 'URL',
    textContent: 'Text',
    emailAddress: 'Email Address',
    phoneNumber: 'Phone',
    networkName: 'Network Name (SSID)',
    password: 'Password',
    wifiPassword: 'WiFi password',
    securityType: 'Security Type',
    subject: 'Subject (Optional)',
    emailSubject: 'Email subject',
    message: 'Message (Optional)',
    smsMessage: 'SMS message',
    noPassword: 'No Password',
    readyToGenerate: 'Ready to generate QR code',
    emailAddressError: 'Please enter a valid email address',
    phoneNumberError: 'Please enter a valid phone number',
    
    // Placeholders
    placeholderUrl: 'https://example.com',
    placeholderText: 'Enter your text here...',
    placeholderEmail: 'user@example.com',
    placeholderPhone: '+1234567890',
    placeholderWifi: 'My WiFi Network',
    
    // Customization
    customize: 'Customize',
    qrCodeColor: 'QR Code Color',
    backgroundColor: 'Background Color',
    size: 'Size',
    errorCorrectionLevel: 'Error Correction Level',
    errorLevelLow: 'Low (~7%)',
    errorLevelMedium: 'Medium (~15%)',
    errorLevelQuartile: 'Quartile (~25%)',
    errorLevelHigh: 'High (~30%)',
    errorLevelDesc: 'Higher levels can recover from more damage but create larger QR codes',
    
    // QR Display
    yourQrCode: 'Your QR Code',
    enterContentAbove: 'Enter content above to generate your QR code',
    
    // Footer
    footerText: '© 2025 QRCodeForge.com - Create, customize and manage your QR codes with ease. Free, fast, and secure.',
    
    // Meta
    meta: {
      title: 'Free QR Code Generator - Custom QR Codes | QRCodeForge',
      description: 'Generate high-quality QR codes for URLs, text, email, WiFi and more. Free generator with custom colors and sizes. No registration required.',
      keywords: 'QR code generator, free QR maker, custom QR codes, URL QR code, WiFi QR code, text QR code, email QR code, restaurant menu QR code'
    },
    metaDescription: 'Generate high-quality QR codes for URLs, text, email, WiFi, restaurant menus and more. Free QR code generator with custom colors, sizes, and error correction. No registration required.',
    metaKeywords: 'QR code generator, free QR code maker, custom QR codes, URL QR code, WiFi QR code, text QR code, email QR code, restaurant menu QR code, bulk QR generator, QR code creator online, barcode generator',
    facebook: 'Facebook',
    facebookProfile: 'Facebook Profile URL',
    facebookShare: 'Share URL to Facebook',
    facebookProfilePlaceholder: 'Enter your Facebook profile URL (e.g., https://www.facebook.com/username)',
    facebookSharePlaceholder: 'Enter the URL you want to share on Facebook',
    invalidFacebookUrl: 'Please enter a valid Facebook URL',
    facebookProfileDesc: 'Generate QR code for your Facebook profile',
    facebookShareDesc: 'Generate QR code for a Facebook post or share',
    facebookUrlPlaceholder: 'Enter Facebook profile or share URL',
    facebookUrlError: 'Please enter a valid Facebook URL',
    facebookProfileUrlError: 'Please enter a valid Facebook profile URL',
    facebookShareUrlError: 'Please enter a valid Facebook share URL',
    websiteUrlError: 'Please enter a valid URL',
    twitter: 'Twitter',
    twitterProfile: 'Twitter Profile',
    twitterShare: 'Share Tweet',
    twitterProfileDesc: 'Generate QR code for your Twitter profile',
    twitterShareDesc: 'Generate QR code to share a tweet',
    twitterProfilePlaceholder: 'Enter @username or full Twitter profile URL',
    twitterSharePlaceholder: 'Enter the tweet text (max 280 characters)',
    twitterProfileError: 'Please enter a valid Twitter username or URL',
    twitterShareError: 'Please enter tweet text (max 280 characters)',
    hiddenNetwork: 'Hidden Network',
  },
  zh: {
    // Header
    title: '二维码生成器',
    subtitle: '为您的商业和个人需求创建精美的自定义二维码。',
    
    // Navigation
    generate: '生成',
    history: '历史记录',
    
    // History Page
    historyPage: {
      title: '二维码历史',
      empty: {
        title: '暂无二维码',
        description: '生成的二维码将显示在这里，方便访问和重新下载。'
      },
      clearAll: '清除全部',
      download: '下载',
      unknownTime: '未知时间',
      qrCodeAlt: '二维码',
      types: {
        url: '网站链接',
        text: '纯文本',
        email: '电子邮件',
        phone: '电话号码',
        sms: '短信',
        wifi: 'WiFi'
      }
    },
    
    // QR Types
    qrCodeType: '二维码类型',
    website: '网站链接',
    websiteDesc: '链接到任何网站',
    plainText: '纯文本',
    plainTextDesc: '任何文本内容',
    email: '邮箱',
    emailDesc: '发送邮件',
    phone: '电话',
    phoneDesc: '拨打电话',
    sms: '短信',
    smsDesc: '发送短信',
    wifi: 'WiFi',
    wifiDesc: '连接WiFi',
    
    // Input Form
    enterContent: '输入您的内容',
    websiteUrl: '网址',
    textContent: '文本',
    emailAddress: '邮箱地址',
    phoneNumber: '电话',
    networkName: '网络名称 (SSID)',
    password: '密码',
    wifiPassword: 'WiFi密码',
    securityType: '安全类型',
    subject: '主题 (可选)',
    emailSubject: '邮件主题',
    message: '消息 (可选)',
    smsMessage: '短信内容',
    noPassword: '无密码',
    readyToGenerate: '准备生成二维码',
    emailAddressError: '请输入有效的邮箱地址',
    phoneNumberError: '请输入有效的手机号',
    
    // Placeholders
    placeholderUrl: 'https://example.com',
    placeholderText: '在此输入您的文本...',
    placeholderEmail: 'user@example.com',
    placeholderPhone: '+86 138 0013 8000',
    placeholderWifi: '我的WiFi网络',
    
    // Customization
    customize: '自定义',
    qrCodeColor: '二维码颜色',
    backgroundColor: '背景颜色',
    size: '尺寸',
    errorCorrectionLevel: '错误纠正级别',
    errorLevelLow: '低 (~7%)',
    errorLevelMedium: '中 (~15%)',
    errorLevelQuartile: '高 (~25%)',
    errorLevelHigh: '最高 (~30%)',
    errorLevelDesc: '更高的级别可以从更多损坏中恢复，但会创建更大的二维码',
    
    // QR Display
    yourQrCode: '您的二维码',
    enterContentAbove: '在上方输入内容以生成您的二维码',
    
    // Footer
    footerText: '© 2025 QRCodeForge.com - 轻松创建、自定义和管理您的二维码。免费、快速、安全。',
    
    // Meta
    meta: {
      title: '免费二维码生成器 - 自定义二维码制作 | QRCodeForge',
      description: '免费生成高质量二维码，支持网址、文本、邮件、WiFi等。支持自定义颜色和尺寸，无需注册。',
      keywords: '二维码生成器, 免费二维码制作, 自定义二维码, 网址二维码, WiFi二维码, 文本二维码, 邮件二维码, 餐厅菜单二维码'
    },
    metaDescription: '免费生成高质量二维码，支持网址、文本、邮件、WiFi、餐厅菜单等。二维码生成器支持自定义颜色、尺寸和错误纠正，无需注册。',
    metaKeywords: '二维码生成器, 免费二维码制作, 自定义二维码, 网址二维码, WiFi二维码, 文本二维码, 邮件二维码, 餐厅菜单二维码, 批量二维码生成器, 在线二维码工具, 条形码生成器',
    facebook: 'Facebook',
    facebookProfile: 'Facebook 个人主页链接',
    facebookShare: 'Facebook 分享链接',
    facebookProfilePlaceholder: '输入你的 Facebook 个人主页链接（例如：https://www.facebook.com/username）',
    facebookSharePlaceholder: '输入你想在 Facebook 上分享的链接',
    invalidFacebookUrl: '请输入有效的 Facebook 链接',
    facebookProfileDesc: '生成您的 Facebook 个人主页二维码',
    facebookShareDesc: '生成 Facebook 帖子或分享的二维码',
    facebookUrlPlaceholder: '输入 Facebook 个人主页或分享链接',
    facebookUrlError: '请输入有效的 Facebook 链接',
    facebookProfileUrlError: '请输入有效的 Facebook 个人主页链接',
    facebookShareUrlError: '请输入有效的 Facebook 分享链接',
    websiteUrlError: '请输入有效的网址',
    twitter: 'Twitter',
    twitterProfile: 'Twitter 主页',
    twitterShare: '推文分享',
    twitterProfileDesc: '生成您的 Twitter 主页二维码',
    twitterShareDesc: '生成用于分享推文的二维码',
    twitterProfilePlaceholder: '输入 @用户名 或完整 Twitter 主页链接',
    twitterSharePlaceholder: '输入推文内容（最多280字符）',
    twitterProfileError: '请输入有效的 Twitter 用户名或链接',
    twitterShareError: '请输入推文内容（最多280字符）',
    hiddenNetwork: '隐藏网络',
  }
};

export const t = (key: string, lang: Language = 'en'): string => {
  const keys = key.split('.');
  let value: TranslationType | string = translations[lang];

  for (const k of keys) {
    if (value === undefined || typeof value !== 'object') {
      console.warn(`Translation key not found: ${key} for language: ${lang}`);
      return key;
    }
    value = value[k];
  }

  if (typeof value !== 'string') {
    console.warn(`Translation value is not a string: ${key} for language: ${lang}`);
    return key;
  }

  return value;
};